import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  computed,
  Input,
  Output,
  EventEmitter,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateService } from '@ngx-translate/core';
import {
  AddFormComponent,
  FormField,
  TableSection,
} from '@shared/components/add-form/add-form.component';
import { WarehouseService } from '../services/warehouse.service';
import {
  VietnamAddressService,
  VietnamProvince,
  VietnamWard,
} from '../services/vietnam-address.service';
import { CountriesService, Country } from '../services/countries.service';
import {
  Warehouse,
  WarehouseCreateRequest,
  WarehouseUpdateRequest,
} from '../models/warehouse.model';
import {
  WarehouseControllerService,
  WarehouseQueryReqDTO,
  PageInfoWarehouseQueryResVO,
  WarehouseQueryResVO,
} from '@app/api/stock-service';
import { vietnamesePhoneNumberValidator } from '@shared/utils/phone-validator.util';
import { WarehouseOptionsService } from '@shared/services/warehouse-options.service';
import { map } from 'rxjs/operators';
import { PaginatedDropdownLoadParams } from '@shared/utils/paginated-dropdown.util';
import { CanComponentDeactivate } from '@app/core/auth/can-component-deactivate.interface';

@Component({
  selector: 'app-warehouse-form',
  standalone: true,
  imports: [CommonModule, AddFormComponent, ToastModule],
  providers: [MessageService],
  templateUrl: './warehouse-form.component.html',
  styleUrl: './warehouse-form.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WarehouseFormComponent
  implements OnInit, OnDestroy, CanComponentDeactivate
{
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private messageService = inject(MessageService);
  private warehouseService = inject(WarehouseService);
  private stockWarehouseController = inject(WarehouseControllerService);
  private readonly warehouseOptionsService = inject(WarehouseOptionsService);
  private vietnamAddressService = inject(VietnamAddressService);
  private countriesService = inject(CountriesService);
  private translateService = inject(TranslateService);
  private destroy$ = new Subject<void>();

  @Input() mode: 'add' | 'edit' = 'add';
  @Input() warehouseId?: number;
  @Output() formSubmit = new EventEmitter<any>();
  @Output() formCancel = new EventEmitter<void>();
  @ViewChild(AddFormComponent) private addFormComponent?: AddFormComponent;

  // Data signals
  loading = signal(false);
  submitting = signal(false);
  warehouseData = signal<Warehouse | null>(null);

  // Form configuration
  formConfig = signal({
    title: '',
    formFields: [] as FormField[],
    headerActions: [] as any[],
    sectionButtons: [] as any[],
    tableSections: [] as TableSection[],
  });

  // Address data
  countries = signal<Country[]>([]);
  provinces = signal<VietnamProvince[]>([]);
  wards = signal<VietnamWard[]>([]);
  selectedProvinceId = signal<number | null>(null);

  // Logical warehouse options
  logicalWarehouseOptions = signal<Array<{ label: string; value: string }>>([]);

  // Track previous values to detect actual changes
  private lastCountryId: string | null = null;
  private lastProvinceId: string | null = null;
  private skipCanDeactivate = false;

  // Computed values
  isEditMode = computed(() => this.mode === 'edit');
  isAddMode = computed(() => this.mode === 'add');

  ngOnInit(): void {
    // Determine mode from route
    this.determineModeFromRoute();

    // Initialize form immediately with header actions
    this.initializeFormConfig();

    // Load countries data and update form fields
    this.loadCountriesData();

    if (this.isEditMode() && this.warehouseId) {
      this.loadWarehouseData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private determineModeFromRoute(): void {
    const url = this.router.url;
    if (url.includes('/edit/')) {
      this.mode = 'edit';
      // Extract warehouse ID from route
      const id = this.route.snapshot.paramMap.get('id');
      if (id) {
        this.warehouseId = +id;
      }
    } else {
      this.mode = 'add';
    }
  }

  private loadCountriesData(): void {
    this.countriesService
      .loadCountries()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (countries) => {
          this.countries.set(countries);
          // Update form fields with country data, but keep existing header actions
          this.updateFormFieldsWithCountries();
        },
        error: (error) => {
          // Still update form fields even if countries fail to load
          this.updateFormFieldsWithCountries();
        },
      });
  }

  private updateFormFieldsWithCountries(): void {
    const currentConfig = this.formConfig();
    const formFields = this.getFormFields();

    // Update only the form fields, keep existing header actions and other config
    this.formConfig.set({
      ...currentConfig,
      formFields: formFields,
    });
  }

  private initializeFormConfig(): void {
    const title = this.isEditMode()
      ? this.translateService.instant('warehouse.form.edit.title')
      : this.translateService.instant('warehouse.form.add.title');

    const formFields = this.getFormFields();
    const headerActions = this.getHeaderActions();

    this.formConfig.set({
      title,
      formFields,
      headerActions,
      sectionButtons: this.getSectionButtons(),
      tableSections: [],
    });

    // Trigger initial country change if country is already selected
    // This handles the case where the form loads with a pre-selected country
    setTimeout(() => {
      const warehouseData = this.warehouseData();
      if (warehouseData && warehouseData.countryId) {
        this.onCountryChange(warehouseData.countryId);
      }
    }, 100);

    // Also trigger country change after countries are loaded to ensure provinces are available
    setTimeout(() => {
      this.onCountryChange(1); // Trigger Vietnam country change (ID: 1)
    }, 200);
  }

  private updateFormConfig(): void {
    // Don't recreate the entire form config as it resets the form values
    // Instead, just update the form fields in place
    const currentConfig = this.formConfig();
    const updatedFormFields = this.getFormFields();

    // Update the form fields without recreating the entire config
    this.formConfig.set({
      ...currentConfig,
      formFields: updatedFormFields,
    });
  }

  private getFormFields(): FormField[] {
    return [
      // Basic Information Section
      {
        label: this.translateService.instant(
          'warehouse.form.fields.warehouse_name'
        ),
        controlName: 'wareName',
        type: 'text',
        required: true,
        placeholder: this.translateService.instant(
          'warehouse.form.placeholders.enter_warehouse_name'
        ),
        maxLength: 255,
        section: this.translateService.instant(
          'warehouse.form.sections.basic_information'
        ),
      },
      {
        label: this.translateService.instant(
          'warehouse.form.fields.warehouse_code'
        ),
        controlName: 'wareCode',
        type: 'text',
        required: true,
        placeholder: this.translateService.instant(
          'warehouse.form.placeholders.enter_warehouse_code'
        ),
        maxLength: 255,
        section: this.translateService.instant(
          'warehouse.form.sections.basic_information'
        ),
      },
      {
        label: this.translateService.instant(
          'warehouse.form.fields.contact_name'
        ),
        controlName: 'contactName',
        type: 'text',
        // required: true,
        placeholder: this.translateService.instant(
          'warehouse.form.placeholders.enter_contact_name'
        ),
        maxLength: 255,
        section: this.translateService.instant(
          'warehouse.form.sections.basic_information'
        ),
      },
      {
        label: this.translateService.instant(
          'warehouse.form.fields.contact_phone'
        ),
        controlName: 'contactPhone',
        type: 'text',
        // required: true,
        placeholder: this.translateService.instant(
          'warehouse.form.placeholders.enter_contact_phone'
        ),
        maxLength: 20,
        section: this.translateService.instant(
          'warehouse.form.sections.basic_information'
        ),
        validators: [vietnamesePhoneNumberValidator],
        errorMessages: {
          invalidVietnamesePhone: this.translateService.instant(
            'common.validation.invalid_phone_format'
          ),
        },
      },
      {
        label: this.translateService.instant('warehouse.form.fields.active'),
        controlName: 'status',
        type: 'checkbox',
        required: false,
        section: this.translateService.instant(
          'warehouse.form.sections.basic_information'
        ),
      },
      // Address Information Section
      {
        label: this.translateService.instant('warehouse.form.fields.country'),
        controlName: 'countryId',
        type: 'dropdown',
        required: true,
        disabled: false, // Explicitly ensure country dropdown is not disabled
        placeholder:
          this.countries().length > 0
            ? this.translateService.instant(
                'warehouse.form.placeholders.select_country'
              )
            : this.translateService.instant(
                'warehouse.form.placeholders.loading_countries'
              ),
        options: this.countries().map((c) => ({
          label: c.full_name_en,
          value: c.id,
        })),
        section: this.translateService.instant(
          'warehouse.form.sections.address_information'
        ),
      },
      {
        label: this.translateService.instant('warehouse.form.fields.province'),
        controlName: 'provinceId',
        type: 'searchableDropdown',
        required: true,
        disabled: false, // Explicitly ensure province dropdown is not disabled
        placeholder:
          this.provinces().length > 0
            ? this.translateService.instant(
                'warehouse.form.placeholders.select_province'
              )
            : this.translateService.instant(
                'warehouse.form.placeholders.no_results_found'
              ),
        paginatedOptions: {
          loadPage: (params: PaginatedDropdownLoadParams) =>
            this.vietnamAddressService.getProvinceOptionsPage(params, 'en'),
        },
        section: this.translateService.instant(
          'warehouse.form.sections.address_information'
        ),
      },
      {
        label: this.translateService.instant(
          'warehouse.form.fields.ward_commune'
        ),
        controlName: 'cityId',
        type: 'searchableDropdown',
        required: true,
        disabled: !this.selectedProvinceId() || this.wards().length === 0, // Disable if no province selected or no wards loaded
        placeholder: !this.selectedProvinceId()
          ? this.translateService.instant(
              'warehouse.form.placeholders.select_province_first'
            )
          : this.wards().length > 0
          ? this.translateService.instant(
              'warehouse.form.placeholders.select_ward_commune'
            )
          : this.translateService.instant(
              'warehouse.form.placeholders.no_results_found'
            ),
        paginatedOptions: {
          loadPage: (params: PaginatedDropdownLoadParams) =>
            this.vietnamAddressService.getWardOptionsPage(
              this.selectedProvinceId(),
              params,
            ),
        },
        section: this.translateService.instant(
          'warehouse.form.sections.address_information'
        ),
      },
      {
        label: this.translateService.instant(
          'warehouse.form.fields.detailed_address'
        ),
        controlName: 'address',
        type: 'text',
        required: true,
        placeholder: this.translateService.instant(
          'warehouse.form.placeholders.enter_detailed_address'
        ),
        maxLength: 255,
        section: this.translateService.instant(
          'warehouse.form.sections.address_information'
        ),
      },
      // Logical Warehouse Section
      {
        label: this.translateService.instant(
          'warehouse.form.fields.logical_warehouse_name'
        ),
        controlName: 'logicWarehouseName',
        type: 'searchableDropdown',
        required: true,
        placeholder: this.translateService.instant(
          'warehouse.form.placeholders.select_logical_warehouse'
        ),
        paginatedOptions: {
          filterBy: 'filterText',
          loadPage: (params: PaginatedDropdownLoadParams) =>
            this.warehouseOptionsService.getOptionsPage(params).pipe(
              map((page) => ({
                ...page,
                options: page.options.map((option) => {
                  const name = option.label.includes(' - ')
                    ? option.label.split(' - ').slice(1).join(' - ')
                    : option.label;
                  return {
                    label: name,
                    value: name,
                    filterText: params.searchText,
                  };
                }),
              })),
            ),
        },
        section: this.translateService.instant(
          'warehouse.form.sections.logical_warehouse'
        ),
      },
      {
        label: this.translateService.instant(
          'warehouse.form.fields.enable_logical_warehouse'
        ),
        controlName: 'logicFlag',
        type: 'checkbox',
        required: false,
        section: this.translateService.instant(
          'warehouse.form.sections.logical_warehouse'
        ),
      },
    ];
  }

  private getHeaderActions(): any[] {
    return [
      {
        label: this.translateService.instant('warehouse.form.actions.cancel'),
        severity: 'secondary',
        outlined: true,
        action: 'cancel',
      },
      {
        label: this.translateService.instant('warehouse.form.actions.save'),
        severity: 'info',
        action: 'save', // Changed from 'submit' to 'save' to match shared component
      },
    ];
  }

  private getSectionButtons(): any[] {
    return [];
  }

  private loadWarehouseData(): void {
    if (!this.warehouseId) return;

    this.loading.set(true);
    this.warehouseService
      .getWarehouseById(this.warehouseId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (warehouse) => {
          this.warehouseData.set(warehouse);

          // Load provinces and wards based on country in edit mode
          if (warehouse.countryId) {
            this.onCountryChange(warehouse.countryId);

            // Set the selected province for edit mode
            if (warehouse.provinceId) {
              this.selectedProvinceId.set(warehouse.provinceId);
            }

            // After country change loads provinces, handle ward loading
            if (warehouse.cityId) {
              // Use a small delay to ensure provinces are loaded first
              setTimeout(() => {
                // First, find the ward by ID to get its actual province
                this.vietnamAddressService
                  .findWardById(warehouse.cityId)
                  .then((ward) => {
                    if (ward) {
                      // Update the warehouse data with the correct province ID
                      const correctedWarehouse = {
                        ...warehouse,
                        provinceId: ward.province_id,
                      };
                      this.warehouseData.set(correctedWarehouse);

                      // Load wards for the correct province
                      this.vietnamAddressService
                        .loadWardsByProvince(ward.province_id)
                        .pipe(takeUntil(this.destroy$))
                        .subscribe({
                          next: (wards) => {
                            this.wards.set(wards);
                            // Update form config to refresh dropdown options
                            this.updateFormConfig();
                          },
                          error: (error) => {
                            // Handle error silently
                          },
                        });
                    } else {
                      // Fallback to original province ID
                      this.vietnamAddressService
                        .loadWardsByProvince(warehouse.provinceId)
                        .pipe(takeUntil(this.destroy$))
                        .subscribe({
                          next: (wards) => {
                            this.wards.set(wards);
                            this.updateFormConfig();
                          },
                          error: (error) => {
                            console.error(
                              'Error loading wards in edit mode:',
                              error
                            );
                          },
                        });
                    }
                  });
              }, 100);
            }
          }

          this.loading.set(false);
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant(
              'warehouse.messages.error.title'
            ),
            detail: this.translateService.instant(
              'warehouse.messages.error.failed_to_load_warehouse'
            ),
            life: 6000,
          });
          this.loading.set(false);
        },
      });
  }

  onFormAction(event: { action: string; data?: any }): void {
    switch (event.action) {
      case 'cancel':
        this.onCancel();
        break;
      case 'save':
        this.onSubmit(event.data);
        break;
    }
  }

  onFormValueChanges(formData: any): void {
    // Handle form value changes
    // Track previous values to detect actual changes
    const currentCountryId = formData.countryId;
    const currentProvinceId = formData.provinceId;

    // Only trigger country change if country actually changed
    if (currentCountryId && currentCountryId !== this.lastCountryId) {
      this.lastCountryId = currentCountryId;
      this.onCountryChange(currentCountryId);
    }

    // Only trigger province change if province actually changed
    if (currentProvinceId && currentProvinceId !== this.lastProvinceId) {
      this.lastProvinceId = currentProvinceId;
      this.selectedProvinceId.set(parseInt(currentProvinceId));
      this.onProvinceChange(currentProvinceId);

      // Clear ward selection when province changes
      if (formData.cityId) {
        // This will be handled by the form component when it detects the change
        // We need to emit a form update to clear the cityId field
        setTimeout(() => {
          this.updateFormConfig();
        }, 100);
      }
    } else if (!currentProvinceId) {
      // Clear selected province if no province is selected
      this.selectedProvinceId.set(null);
      this.wards.set([]);
      this.updateFormConfig();
    }
  }

  onSectionAction(action: any): void {
    if (action.action === 'submit') {
      this.onSubmit();
    } else if (action.action === 'cancel') {
      this.onCancel();
    }
  }

  onTableAction(action: any): void {
    // Handle table actions if needed
  }

  onSubmit(formData?: any): void {
    if (!formData) {
      this.submitting.set(false);
      return;
    }

    this.submitting.set(true);

    // Validate required fields
    if (
      !formData.wareName ||
      !formData.wareCode ||
      !formData.contactName ||
      !formData.contactPhone ||
      !formData.countryId ||
      !formData.provinceId ||
      !formData.cityId ||
      !formData.address ||
      !formData.logicWarehouseName
    ) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant(
          'warehouse.messages.error.title'
        ),
        detail: this.translateService.instant(
          'warehouse.messages.validation.fill_required_fields'
        ),
        life: 6000,
      });
      this.submitting.set(false);
      return;
    }

    // Prepare the warehouse data
    const warehouseData: WarehouseCreateRequest = {
      wareName: formData.wareName,
      wareCode: formData.wareCode,
      contactName: formData.contactName,
      contactPhone: formData.contactPhone,
      countryId: formData.countryId || 0,
      provinceId: formData.provinceId || 0,
      cityId: formData.cityId || 0,
      address: formData.address || formData.additionalValues?.address || '',
      logicWarehouseName: formData.logicWarehouseName || '',
      logicFlag: formData.logicFlag || false,
      status: formData.status !== undefined ? formData.status : true, // Default to true if not specified
      deleteFlag: false,
      tenantId: 1,
    };

    if (this.isEditMode()) {
      // Edit mode - update existing warehouse
      const updateData: WarehouseUpdateRequest = {
        id: this.warehouseId!,
        ...warehouseData,
        tenantId: 1, // Default tenant ID
        deleteFlag: false,
        status: formData.status !== undefined ? formData.status : true,
      };

      this.warehouseService.updateWarehouse(updateData).subscribe({
        next: (updatedWarehouse) => {
          this.submitting.set(false);
          this.skipCanDeactivate = true;
          this.addFormComponent?.form?.markAsPristine();

          // Emit success event through service
          this.warehouseService.emitWarehouseOperationResult({
            success: true,
            message: this.translateService.instant(
              'warehouse.messages.success.warehouse_updated'
            ),
            type: 'update',
          });

          // Navigate immediately after success
          this.router.navigate(['/warehouse/channel']);
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant(
              'warehouse.messages.error.title'
            ),
            detail: this.translateService.instant(
              'warehouse.messages.error.failed_to_update_warehouse'
            ),
            life: 6000,
          });
          this.submitting.set(false);
        },
      });
    } else {
      // Add mode - create new warehouse
      this.warehouseService.createWarehouse(warehouseData).subscribe({
        next: (createdWarehouse) => {
          this.submitting.set(false);
          this.skipCanDeactivate = true;
          this.addFormComponent?.form?.markAsPristine();

          // Emit success event through service
          this.warehouseService.emitWarehouseOperationResult({
            success: true,
            message: this.translateService.instant(
              'warehouse.messages.success.warehouse_created'
            ),
            type: 'create',
          });

          // Navigate immediately after success
          this.router.navigate(['/warehouse/channel']);
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant(
              'warehouse.messages.error.title'
            ),
            detail: this.translateService.instant(
              'warehouse.messages.error.failed_to_create_warehouse'
            ),
            life: 6000,
          });
          this.submitting.set(false);
        },
      });
    }
  }

  onCancel(): void {
    this.router.navigate(['/warehouse/channel']);
  }

  canDeactivate(
    _currentRoute: ActivatedRouteSnapshot,
    _currentState: RouterStateSnapshot,
    _nextState?: RouterStateSnapshot
  ): boolean | UrlTree {
    if (this.skipCanDeactivate || this.submitting()) {
      return true;
    }

    return !(this.addFormComponent?.form?.dirty ?? false);
  }

  private onCountryChange(countryId: string | number): void {
    // Reset province and ward data immediately
    this.provinces.set([]);
    this.wards.set([]);
    this.selectedProvinceId.set(null);
    this.lastProvinceId = null; // Reset province tracking

    // Check if it's Vietnam (ID: 1 or "1" or "VN")
    if (countryId === 1 || countryId === '1' || countryId === 'VN') {
      // Load Vietnamese provinces for Vietnam
      this.vietnamAddressService
        .loadProvinces()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (provinces) => {
            this.provinces.set(provinces);
            // Update form config to refresh dropdown options
            this.updateFormConfig();
          },
          error: (error) => {
            // Handle error silently
          },
        });
    } else {
      // For other countries, provinces and wards are already cleared above
      // Update form config to show "No results found" for province/ward
      this.updateFormConfig();
    }
  }

  private onProvinceChange(provinceId: number): void {
    // Reset ward data when province changes
    this.wards.set([]);

    if (provinceId) {
      this.vietnamAddressService
        .loadWardsByProvince(provinceId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (wards) => {
            this.wards.set(wards);
            // Update form config to refresh dropdown options with a small delay
            // to ensure form values are properly set
            setTimeout(() => {
              this.updateFormConfig();
            }, 50);
          },
          error: (error) => {
            console.error('Error loading wards:', error);
            // Update form config even on error to show disabled state
            this.updateFormConfig();
          },
        });
    } else {
      // Update form config to show "No results found" for ward
      this.updateFormConfig();
    }
  }
}
